import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { requireTab } from "@/features/navigation/requireTab";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { formNameLabel, schedulePreviewLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useScheduleQuery } from "@/features/schedules/api/selectSchedule";
import { useChangeTrailQuery } from "@/features/validations/api/selectChangeTrail";
import ChangeTrailView from "@/features/validations/components/ChangeTrailView";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { z } from "zod";

const changeTrailSearchSchema = z.object({
    since: z.string().optional(),
});

function ChangeTrailPage() {
    const { entityType, entityId } = Route.useParams();
    const { since } = Route.useSearch();
    const { locale } = useAppLocale();
    const { error } = useChangeTrailQuery(entityType, entityId, since, true);
    const { data: form } = useSelectFormById(entityType === "form" ? entityId : "skip", locale);
    const { data: schedule } = useScheduleQuery(entityType === "schedule" ? entityId : "");
    const identity = entityType === "form" ? formNameLabel(form) : schedulePreviewLabel(schedule?.timings, locale);

    useNav([
        { label: "Validate", route: { to: "/validate" } },
        { label: "Change trail", route: { to: "/validate" } },
        {
            label: identity,
            route: {
                to: "/validate/change-trail/$entityType/$entityId",
                params: { entityType, entityId },
                ...(since ? { search: { since } } : {}),
            },
        },
    ]);

    return (
        <div className="mx-auto p-4 w-full min-w-0 max-w-xl">
            <h1 className="mb-1 font-semibold text-xl">Change trail</h1>
            <p className="mb-6 text-muted-foreground text-sm">
                {since
                    ? `Each page and question changed since this validation was resolved.`
                    : `Each page and question that changed on this ${entityType}.`}
            </p>
            {error ? (
                <p className="text-muted-foreground text-sm">
                    {(error as Error).message || "Could not load this change trail."}
                </p>
            ) : (
                <ChangeTrailView entityType={entityType} entityId={entityId} since={since} />
            )}
            <ui.Button asChild variant="secondary" size="lg" className="mt-6">
                <Link to="/validate">
                    <ArrowLeft className="w-4 h-4" />
                    Back to your list
                </Link>
            </ui.Button>
        </div>
    );
}

export const Route = createFileRoute("/_app/validate/change-trail/$entityType/$entityId")({
    component: ChangeTrailPage,
    validateSearch: changeTrailSearchSchema,
    params: {
        parse: (params) => ({
            entityType: params.entityType as "form" | "schedule",
            entityId: params.entityId,
        }),
    },
    beforeLoad: async ({ context }) => {
        const userId = context?.auth?.id;
        if (!userId || !context?.queryClient) {
            throw redirect({ to: "/login" });
        }

        const roles = await context.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(userId));
        context.auth.roles.setRoles(roles ?? []);

        await requireTab(context, "validate");

        return context;
    },
});
